import React from 'react';
import { useTranslation } from 'react-i18next';

import { WireBankDetails, WireBranding, pickLocalizedText } from '../../../common/models/billing.model';
import ardealyLogo from '../../../assets/ardealy-mark.svg';

export const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

/** Colors of a printed invoice page — the PDF is always on white paper, so the
 *  preview stays white regardless of the app's theme. Kept as literals on
 *  purpose: they mirror the pdfkit renderer, not the app's design tokens. */
const PAPER = { bg: '#FFFFFF', text: '#111827', muted: '#6B7280', faint: '#9CA3AF', line: '#E5E7EB' };

/** The accent the PDF falls back to when none (or an invalid one) is set. */
export const PDF_DEFAULT_ACCENT = PAPER.text;

/**
 * Live, scaled approximation of the invoice PDF. Mirrors the pdfkit renderer's
 * structure (two-column identity header, invoice metadata strip, rounded
 * bordered line-item table, 2-column bank details grid) so admins can preview
 * their branding + bank changes as they would land on the real PDF.
 *
 * The words on the page are the PDF's (ADMIN_BILLING.PREVIEW mirrors the API's
 * invoice-i18n) and render in `lang` — the language being edited — so the
 * preview reads like the invoice a vendor in that language receives.
 */
export const InvoicePreview: React.FC<{ branding?: WireBranding; bankDetails?: WireBankDetails; lang?: string }> = ({
  branding,
  bankDetails,
  lang = 'en',
}) => {
  const { t } = useTranslation();
  const pt = (key: string, options: Record<string, unknown> = {}): string =>
    t(`ADMIN_BILLING.PREVIEW.${key}`, { lng: lang, ...options }) as string;

  // Accent fallback aligned to the PDF's default ink.
  const accent = HEX_COLOR.test(branding?.accentColor || '') ? (branding!.accentColor as string) : PDF_DEFAULT_ACCENT;
  const invNumber = 'INV-000024';
  const vendorName = pt('SAMPLE_VENDOR');
  const mockVendor = {
    name: vendorName,
    address: `${vendorName}\n123 Rue de Test\n75001 Paris\nFrance`,
    email: 'vendor@example.com',
  };
  // Bank rows — same order + labels as the PDF renderer.
  const bankRowsAll = [
    [pt('PAYMENT_REFERENCE'), invNumber],
    [pt('ACCOUNT_NAME'), bankDetails?.accountName],
    [pt('BANK'), bankDetails?.bankName],
    [pt('ACCOUNT_NUMBER'), bankDetails?.accountNumber],
    [pt('IBAN'), bankDetails?.iban],
    [pt('SWIFT'), bankDetails?.swift],
    [pt('ROUTING_NUMBER'), bankDetails?.routingNumber],
    [pt('ADDRESS'), bankDetails?.address],
  ].filter(([, v]) => !!v) as [string, string][];
  const hasBankRows = bankRowsAll.length > 1;
  // Chunk into pairs for the 2-column grid.
  const bankPairs: Array<[[string, string], [string, string] | null]> = [];
  for (let i = 0; i < bankRowsAll.length; i += 2) {
    bankPairs.push([bankRowsAll[i], bankRowsAll[i + 1] || null]);
  }

  // The PDF renders in Manrope; the preview matches it, not the app font.
  const fontFamily = 'Manrope, sans-serif';

  return (
    <div
      className="shadow-sm ring-1 ring-foreground/10"
      data-testid="invoice-preview"
      style={{
        backgroundColor: PAPER.bg,
        borderRadius: 2,
        color: PAPER.text,
        fontFamily,
        // A4 portrait page (210 × 297mm).
        width: '100%',
        maxWidth: 460,
        aspectRatio: '210 / 297',
        marginInline: 'auto',
        paddingInline: 28,
        paddingBlock: 32,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ── Two-column identity header — seller LEFT, vendor RIGHT ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20 }}>
        {/* Seller (ARdealy) column */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <img src={ardealyLogo} alt="ARdealy" style={{ height: 34, width: 'auto', maxWidth: 120, objectFit: 'contain', alignSelf: 'flex-start' }} />
          <div style={{ marginTop: 6 }}>
            {branding?.companyAddress && <p style={{ margin: 0, fontSize: 9, color: PAPER.muted, whiteSpace: 'pre-line' }}>{branding.companyAddress}</p>}
            {branding?.taxId && <p style={{ margin: 0, fontSize: 9, color: PAPER.muted }}>{pt('TAX_ID')}: {branding.taxId}</p>}
            {branding?.registrationNumber && <p style={{ margin: 0, fontSize: 9, color: PAPER.muted }}>{pt('REG_NO')}: {branding.registrationNumber}</p>}
          </div>
        </div>
        {/* Vendor column — logo + identity, right-aligned */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, minWidth: 0 }}>
          {/* Placeholder — real vendor logo is per-invoice, sourced from configs.logo */}
          <div style={{ height: 34, width: 60, border: `1px dashed ${PAPER.line}`, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p style={{ margin: 0, fontSize: 7, color: PAPER.faint, textAlign: 'center', lineHeight: 1.2 }}>{pt('VENDOR_LOGO')}</p>
          </div>
          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: PAPER.text }}>{mockVendor.name}</p>
            <p style={{ margin: 0, fontSize: 9, color: PAPER.muted, whiteSpace: 'pre-line', textAlign: 'right' }}>{mockVendor.address}</p>
            <p style={{ margin: 0, fontSize: 9, color: PAPER.muted }}>{mockVendor.email}</p>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div style={{ height: 1, backgroundColor: PAPER.line, marginBlock: 12 }} />

      {/* ── Invoice metadata strip ── */}
      <div style={{ marginBottom: 12 }}>
        <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: accent }}>{pt('INVOICE')} {invNumber}</p>
        <p style={{ margin: 0, marginTop: 2, fontSize: 10, color: PAPER.muted }}>
          {pt('STATUS')}: {pt('AWAITING_PAYMENT')}  ·  {pt('ISSUED')}: 2026-08-23  ·  {pt('DUE')}: 2026-09-22
        </p>
      </div>

      {/* ── Line-item table (rounded bordered card, dark header clipped by overflow) ── */}
      <div style={{ border: `1px solid ${PAPER.line}`, borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
        {/* Header strip */}
        <div style={{ backgroundColor: accent, color: PAPER.bg, paddingInline: 12, paddingBlock: 6, display: 'flex', justifyContent: 'space-between' }}>
          <p style={{ margin: 0, fontSize: 10, fontWeight: 700 }}>{pt('DESCRIPTION')}</p>
          <p style={{ margin: 0, fontSize: 10, fontWeight: 700 }}>{pt('AMOUNT')}</p>
        </div>
        {/* Item row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingInline: 12, paddingBlock: 8 }}>
          <div>
            <p style={{ margin: 0, fontSize: 11, color: PAPER.text }}>{pt('PLAN_LINE', { plan: 'Pro' })}</p>
            <p style={{ margin: 0, fontSize: 9, color: PAPER.muted }}>{pt('PERIOD', { start: '2026-08-23', end: '2026-09-23' })}</p>
          </div>
          <p style={{ margin: 0, fontSize: 11, color: PAPER.text }}>$750</p>
        </div>
        {/* Inner divider */}
        <div style={{ height: 1, backgroundColor: PAPER.line }} />
        {/* Total row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', paddingInline: 12, paddingBlock: 8 }}>
          <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: PAPER.text }}>{pt('TOTAL_DUE')}</p>
          <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: PAPER.text }}>$750</p>
        </div>
      </div>

      {/* ── Bank details + footer — pinned to bottom ── */}
      <div style={{ marginTop: 'auto' }}>
        <div style={{ height: 1, backgroundColor: PAPER.line, marginBottom: 10 }} />
        <p style={{ margin: 0, marginBottom: 6, fontSize: 11, fontWeight: 700, color: accent }}>{pt('BANK_TRANSFER_DETAILS')}</p>
        {!hasBankRows ? (
          <p style={{ margin: 0, fontSize: 10, color: PAPER.muted }}>{t('ADMIN_BILLING.SETTINGS.PREVIEW_BANK_EMPTY')}</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {bankPairs.map(([leftCell, rightCell], i) => (
              <div key={i} style={{ display: 'flex', gap: 16 }}>
                <div style={{ flex: 1, display: 'flex', gap: 8, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 9, fontWeight: 700, color: PAPER.muted, minWidth: 72, flexShrink: 0 }}>{leftCell[0]}</p>
                  <p style={{ margin: 0, fontSize: 9, color: PAPER.text, wordBreak: 'break-word', flex: 1 }}>{leftCell[1]}</p>
                </div>
                <div style={{ flex: 1, display: 'flex', gap: 8, minWidth: 0 }}>
                  {rightCell && (
                    <>
                      <p style={{ margin: 0, fontSize: 9, fontWeight: 700, color: PAPER.muted, minWidth: 72, flexShrink: 0 }}>{rightCell[0]}</p>
                      <p style={{ margin: 0, fontSize: 9, color: PAPER.text, wordBreak: 'break-word', flex: 1 }}>{rightCell[1]}</p>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <p style={{ margin: 0, marginTop: 14, fontSize: 8, color: PAPER.faint, textAlign: 'center' }}>
          {pickLocalizedText(branding?.footerText, lang) || pt('FOOTER')}
        </p>
      </div>
    </div>
  );
};
